import { useState, type DragEvent, type KeyboardEvent, type ReactNode } from 'react'
import { Plus, RotateCcw, X } from 'lucide-react'
import { Button, HelpIcon, NumberField, Switch } from '../../core/index.js'
import { filledPctOf, monthLabel, useMonthHealth } from '../../schedule/index.js'
import { DEFAULT_CENSUS_RESPONSE_RULES, DEFAULT_RELEASE_RULES, DEFAULT_SCHEDULING_RULES, DEFAULT_STAFF_ALERT_RULES } from '../defaultSchedulingRules.js'
import type { AddStep, CensusResponseRules, FlexStep, ReleaseRules, SchedulingRules } from '../types.js'

/**
 * The Automation tab of Configuration (facility operations dashboard plan §3, §5, §6; laid out as the plan's mockup,
 * screen 3): what the engine does on its own and where it stops and asks - how much of the month may still be open at
 * release, who releases it and what holds it back, what happens when the census moves, how open shifts are offered,
 * and what the facility's staff are told. One card of groups, each with its title and reason on the left and its
 * controls on the right. Part of the same draft and Save as the other settings tabs; read through `schedulingRulesOf`.
 */

const SELECT_CLASS = 'rounded border border-marine/30 bg-white px-2 py-1.5 text-sm font-bold text-ink focus:outline-none focus:border-marine focus:ring-2 focus:ring-marine/20'
const TIME_CLASS = 'w-24 rounded border border-marine/30 bg-white px-2 py-1 text-sm font-bold text-ink focus:outline-none focus:border-marine'

const FLEX_PILL_LABELS: Record<FlexStep, string> = {
  volunteers: 'Volunteers',
  agency: 'Agency',
  prn: 'PRN',
  tier: 'Lowest tier',
  rotation: 'Fewest flexes',
  seniority: 'Least senior',
  overtime: 'Past overtime that week',
  part_time: 'Part-time',
}
const ALL_FLEX_STEPS: readonly FlexStep[] = ['volunteers', 'agency', 'prn', 'tier', 'rotation', 'seniority', 'overtime', 'part_time']

const ADD_PILL_LABELS: Record<AddStep, string> = { overtime: 'Full-time', part_time: 'Part-time', prn: 'PRN', agency: 'Agency' }
const ALL_ADD_STEPS: readonly AddStep[] = ['overtime', 'part_time', 'prn', 'agency']

const TIE_BREAK_WORDS: Record<CensusResponseRules['tieBreak'][number], string> = { score: 'lowest score', seniority: 'least senior' }

/** One group of the card: the title, its help and why it matters on the left, the controls on the right. */
function AutomationGroup({ title, help, intro, children }: { title: string; help: string; intro: ReactNode; children: ReactNode }) {
  return (
    <div className="grid gap-4 py-5 border-b border-marine/10 last:border-b-0 md:grid-cols-[260px_minmax(0,1fr)] md:gap-6">
      <div>
        <h3 className="flex items-center gap-1 text-[13px] font-black text-ink">
          {title}
          <HelpIcon topic={help} />
        </h3>
        <p className="mt-1 text-[11px] text-ink-mid leading-relaxed">{intro}</p>
      </div>
      <div className="flex flex-col gap-3 min-w-0">{children}</div>
    </div>
  )
}

/** A choice drawn as a card: the radio, a bold name and what it means. */
function ChoiceCard({ name, checked, title, recommended, onSelect, children }: { name: string; checked: boolean; title: string; recommended?: boolean; onSelect: () => void; children: ReactNode }) {
  return (
    <label className={`flex gap-2.5 items-start rounded border px-3 py-2 text-xs leading-snug cursor-pointer ${checked ? 'border-marine bg-marine-lt' : 'border-marine/20 bg-white hover:border-marine/40'}`}>
      <input type="radio" name={name} checked={checked} onChange={onSelect} className="mt-0.5 accent-marine" />
      <span className="text-ink">
        <b className="block">{title}</b>
        {children}
        {recommended && <span className="text-[11px] text-ink-mid"> Recommended</span>}
      </span>
    </label>
  )
}

/** A bold label beside its number box, for the rows of numbers set side by side. */
function InlineNumber({ label, value, onChange, suffix }: { label: string; value: number; onChange: (value: number) => void; suffix?: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="text-xs font-bold text-ink">{label}</span>
      <NumberField value={value} onChange={onChange} label={label} suffix={suffix} />
    </span>
  )
}

/** A switch with what it switches written beside it. */
/** A number that may be off: empty means null. */
function OptionalNumber({ label, value, min, onChange }: { label: string; value: number | null; min: number; onChange: (value: number | null) => void }) {
  return <input type="number" min={min} aria-label={label} placeholder="Off" className="w-16 rounded border border-marine/30 bg-white px-2 py-1 text-right text-sm font-bold text-ink placeholder:font-normal placeholder:text-ink-mid/60 focus:outline-none focus:border-marine" value={value ?? ''} onChange={(event) => onChange(event.target.value === '' ? null : Math.max(min, Number(event.target.value)))} />
}

function SwitchLine({ label, checked, onChange, children }: { label: string; checked: boolean; onChange: (checked: boolean) => void; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-ink">
      <Switch checked={checked} onChange={onChange} label={label} />
      <span>{label}</span>
      {children}
    </div>
  )
}

/** A pill the reader presses, pressed or not. */
function Pill({ pressed, onClick, children }: { pressed: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" aria-pressed={pressed} onClick={onClick} className={`inline-flex items-center rounded border px-2.5 py-1.5 text-xs font-bold ${pressed ? 'border-marine bg-marine-lt text-marine-dk' : 'border-marine/25 bg-white text-ink-mid/70'}`}>
      {children}
    </button>
  )
}

/**
 * An order as numbered pills: drag one to move it, or focus it and use the arrow keys; × takes a step out (never the
 * last) and the dashed pills put one back at the end.
 */
function OrderPills<T extends string>({ label, order, all, labels, onChange }: { label: string; order: readonly T[]; all: readonly T[]; labels: Record<T, string>; onChange: (order: T[]) => void }) {
  const [dragged, setDragged] = useState<number | null>(null)
  const moveTo = (from: number, to: number): void => {
    if (from === to || to < 0 || to >= order.length) {
      return
    }
    const rest = order.filter((_, index) => index !== from)
    onChange([...rest.slice(0, to), order[from] as T, ...rest.slice(to)])
  }
  const onKey = (event: KeyboardEvent, index: number): void => {
    const by = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : 0
    if (by !== 0) {
      event.preventDefault()
      moveTo(index, index + by)
    }
  }
  const onDrop = (event: DragEvent, index: number): void => {
    event.preventDefault()
    if (dragged !== null) {
      moveTo(dragged, index)
    }
    setDragged(null)
  }
  const unused = all.filter((step) => !order.includes(step))
  return (
    <ol aria-label={label} className="flex flex-wrap items-center gap-2">
      {order.map((step, index) => (
        <li
          key={step}
          draggable
          tabIndex={0}
          aria-label={`${index + 1}. ${labels[step]} - arrow keys move it`}
          onDragStart={() => setDragged(index)}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => onDrop(event, index)}
          onDragEnd={() => setDragged(null)}
          onKeyDown={(event) => onKey(event, index)}
          className={`inline-flex items-center gap-1.5 rounded border bg-white pl-2.5 pr-1 py-1 text-xs font-bold text-ink cursor-grab focus:outline-none focus-visible:ring-2 focus-visible:ring-marine/40 ${dragged === index ? 'border-marine opacity-60' : 'border-marine/25'}`}
        >
          <span className="text-marine">{index + 1}</span>
          {labels[step]}
          <button type="button" aria-label={`Take out ${labels[step]}`} disabled={order.length === 1} onClick={() => onChange(order.filter((entry) => entry !== step))} className="p-0.5 text-ink-mid/60 hover:text-coral disabled:opacity-30">
            <X className="h-3 w-3" />
          </button>
        </li>
      ))}
      {unused.map((step) => (
        <li key={step}>
          <button type="button" onClick={() => onChange([...order, step])} className="inline-flex items-center gap-1 rounded border border-dashed border-marine/30 px-2 py-1 text-xs text-ink-mid hover:border-marine hover:text-marine">
            <Plus className="h-3 w-3" />
            {labels[step]}
          </button>
        </li>
      ))}
    </ol>
  )
}

/** The rules this tab owns put back to the API's defaults; everything else in the block is left as it stands. */
function withAutomationDefaults(rules: SchedulingRules): SchedulingRules {
  const coverage = DEFAULT_SCHEDULING_RULES.coverage
  return {
    ...rules,
    release: DEFAULT_RELEASE_RULES,
    census: DEFAULT_CENSUS_RESPONSE_RULES,
    alerts: DEFAULT_STAFF_ALERT_RULES,
    coverage: { ...rules.coverage, lockHoursBefore: coverage.lockHoursBefore, openBoardOfferHours: coverage.openBoardOfferHours, openBoardClosesHoursBefore: coverage.openBoardClosesHoursBefore },
    bridge: { ...rules.bridge, escalateAfterMinutes: DEFAULT_SCHEDULING_RULES.bridge.escalateAfterMinutes },
  }
}

export function FacilityAutomationCard({ facilityId, rules, onChange }: { facilityId: string | null; rules: SchedulingRules; onChange: (rules: SchedulingRules) => void }) {
  const update = (patch: Partial<SchedulingRules>): void => onChange({ ...rules, ...patch })
  const release: ReleaseRules = { ...DEFAULT_RELEASE_RULES, ...(rules.release ?? {}) }
  const updateRelease = (patch: Partial<ReleaseRules>): void => update({ release: { ...release, ...patch } })
  const census: CensusResponseRules = { ...DEFAULT_CENSUS_RESPONSE_RULES, ...(rules.census ?? {}) }
  const updateCensus = (patch: Partial<CensusResponseRules>): void => update({ census: { ...census, ...patch } })
  const alerts = { ...DEFAULT_STAFF_ALERT_RULES, ...(rules.alerts ?? {}) }
  const coverage = { ...DEFAULT_SCHEDULING_RULES.coverage, ...rules.coverage }
  const updateCoverage = (patch: Partial<SchedulingRules['coverage']>): void => update({ coverage: { ...coverage, ...patch } })
  const mode = release.mode ?? 'after_review'
  const minFilledPct = release.minFilledPct === undefined ? 20 : release.minFilledPct
  const health = useMonthHealth(facilityId, null, false).data
  const flexLabels: Record<FlexStep, string> = { ...FLEX_PILL_LABELS, tier: ['Lowest tier', ...census.tieBreak.map((key) => TIE_BREAK_WORDS[key])].join(', ') }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="text-[22px] font-black text-ink">Automation</h2>
        <span className="text-[11px] text-ink-mid">What the engine does on its own, and where it stops and asks. One tab, read by every other screen.</span>
      </div>

      <section className="bg-white border border-marine/15 rounded-md px-5 pb-2">
        <AutomationGroup title="Open shifts at release" help="facilities.automation" intro="Most facilities cannot staff every seat from their own roster until they hire. This is the share of the month’s required seats the roster must fill before the month goes to workers; the engine fills the rest after the release from available staff - PRN, agency and the open board - as the census firms up.">
          {minFilledPct === null ? (
            <div className="flex flex-wrap items-center gap-3 text-xs text-ink">
              <span className="font-bold">No minimum:</span> the release never waits on open shifts.
              <Button icon={Plus} variant="secondary" size="sm" onClick={() => updateRelease({ minFilledPct: 20 })}>
                Set a minimum
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <label htmlFor="min-filled-pct" className="text-[10px] font-bold uppercase tracking-wider text-ink-mid">
                At least
              </label>
              <input id="min-filled-pct" type="range" min={0} max={100} value={minFilledPct} onChange={(event) => updateRelease({ minFilledPct: Number(event.target.value) })} className="w-64 max-w-full accent-marine" />
              <span className="text-[22px] font-black text-ink">{minFilledPct}%</span>
              <span className="text-[11px] text-ink-mid">filled from the roster before release · up to {100 - minFilledPct}% left open</span>
              <button type="button" onClick={() => updateRelease({ minFilledPct: null })} className="text-[11px] text-marine underline hover:text-marine-dk">
                No minimum
              </button>
            </div>
          )}
          <p className="text-[11px] text-ink-mid leading-relaxed">
            Below it the release waits for you: assign, open seats to agency, or release past it with a reason.
            {health?.fill ? ` ${monthLabel(health.period).split(' ')[0]} stands at ${filledPctOf(health.fill)}% filled.` : ''} The workforce report says what hiring would close the gap for good.
          </p>
        </AutomationGroup>

        <AutomationGroup title="Releasing the month" help="facilities.automation" intro="Who releases a built month to the workers, and when.">
          <div className="grid gap-2.5 md:grid-cols-3">
            <ChoiceCard name="release-mode" checked={mode === 'after_review'} title="After a review window" recommended onSelect={() => updateRelease({ mode: 'after_review' })}>
              Built at the close; released by the engine when the window ends, unless a block stands or staff hold it.
            </ChoiceCard>
            <ChoiceCard name="release-mode" checked={mode === 'at_close'} title="At the close" onSelect={() => updateRelease({ mode: 'at_close' })}>
              Built and released at once. Nothing to review before workers see it.
            </ChoiceCard>
            <ChoiceCard name="release-mode" checked={mode === 'staff'} title="Only by staff" onSelect={() => updateRelease({ mode: 'staff' })}>
              Someone presses Release.
            </ChoiceCard>
          </div>
          {mode === 'after_review' && (
            <div className="flex flex-wrap items-center gap-2">
              <InlineNumber label="Review window" value={release.reviewDays ?? 4} onChange={(reviewDays) => updateRelease({ reviewDays: Math.min(30, Math.max(0, Math.round(reviewDays))) })} suffix="days" />
              <span className="text-[11px] text-ink-mid">after the build · a block always holds it</span>
            </div>
          )}
          <div className="flex flex-col gap-2">
            <span className="text-xs font-bold text-ink">A block holds it</span>
            <SwitchLine label="A minimum staffing line short" checked={release.requireMinimumStaffing} onChange={(requireMinimumStaffing) => updateRelease({ requireMinimumStaffing })} />
            <SwitchLine label="A seat short of the staffing plan" checked={release.requireRequirement} onChange={(requireRequirement) => updateRelease({ requireRequirement })}>
              <span className="font-normal text-[11px] text-ink-mid">off, it only warns</span>
            </SwitchLine>
            <SwitchLine label="A run behind its inputs" checked={release.requireFreshRun} onChange={(requireFreshRun) => updateRelease({ requireFreshRun })} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold text-ink">Who may release past a block</span>
            <Pill pressed={release.override !== 'none'} onClick={() => updateRelease({ override: release.override === 'none' ? 'operator' : 'none' })}>
              Operator
            </Pill>
            <Pill pressed={release.override === 'staff'} onClick={() => updateRelease({ override: release.override === 'staff' ? 'operator' : 'staff' })}>
              Facility administrator
            </Pill>
            <Pill pressed={release.override === 'none'} onClick={() => updateRelease({ override: 'none' })}>
              Nobody
            </Pill>
            <span className="text-[11px] text-ink-mid">with a reason, recorded on the run and in the activity feed</span>
          </div>
        </AutomationGroup>

        <AutomationGroup title="When the census changes" help="facilities.scheduling-census-response" intro="A recorded count re-evaluates the day against the staffing plan. A fall releases shifts, lowest reliability tier first; a rise offers held seats back and posts new ones.">
          <div className="grid gap-2.5 md:grid-cols-2">
            <ChoiceCard name="census-mode" checked={census.mode === 'automatic'} title="Automatic" recommended onSelect={() => updateCensus({ mode: 'automatic' })}>
              Applied at once and the workers told. When the shift starts within the notice hours or the lock hours, it is proposed instead.
            </ChoiceCard>
            <ChoiceCard name="census-mode" checked={census.mode === 'propose'} title="Propose" onSelect={() => updateCensus({ mode: 'propose' })}>
              Every change waits for Apply on the Today screen.
            </ChoiceCard>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <InlineNumber label="Notice hours" value={census.flexNoticeHours} onChange={(value) => updateCensus({ flexNoticeHours: Math.max(0, value) })} />
            <InlineNumber label="Lock hours before a shift" value={rules.coverage.lockHoursBefore} onChange={(value) => update({ coverage: { ...rules.coverage, lockHoursBefore: Math.max(0, value) } })} />
            <span className="inline-flex items-center gap-2">
              <span className="text-xs font-bold text-ink">Flexes per worker a month</span>
              <input type="number" min={1} aria-label="Flexes per worker a month" placeholder="No cap" className="w-20 rounded border border-marine/30 bg-white px-2 py-1.5 text-right text-sm font-bold text-ink placeholder:font-normal placeholder:text-ink-mid/60 focus:outline-none focus:border-marine focus:ring-2 focus:ring-marine/20" value={census.maxFlexPerWorkerPerPeriod ?? ''} onChange={(event) => updateCensus({ maxFlexPerWorkerPerPeriod: event.target.value === '' ? null : Math.max(1, Math.round(Number(event.target.value))) })} />
            </span>
            <InlineNumber label="Recall hours" value={census.recallHours} onChange={(value) => updateCensus({ recallHours: Math.max(0, value) })} />
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-xs font-bold text-ink">Release order</span>
            <OrderPills label="Release order" order={census.flexOrder} all={ALL_FLEX_STEPS} labels={flexLabels} onChange={(flexOrder) => updateCensus({ flexOrder })} />
            <SwitchLine label="Float to another unit first" checked={census.floatFirst} onChange={(floatFirst) => updateCensus({ floatFirst })} />
          </div>
          <details className="text-xs text-ink">
            <summary className="cursor-pointer text-[11px] font-bold text-marine">More: the tie-break, what a worker is told, the tolerance</summary>
            <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-2">
              <label className="inline-flex items-center gap-2 font-bold">
                Tie-break inside a step
                <select className={SELECT_CLASS} value={census.tieBreak.join(',')} onChange={(event) => updateCensus({ tieBreak: event.target.value.split(',') as CensusResponseRules['tieBreak'] })}>
                  <option value="score,seniority">Lowest score, then least senior</option>
                  <option value="seniority,score">Least senior, then lowest score</option>
                  <option value="score">Lowest score only</option>
                  <option value="seniority">Least senior only</option>
                </select>
              </label>
              <label className="inline-flex items-center gap-2 font-bold">
                A flexed worker is told
                <select className={SELECT_CLASS} value={census.tellWorkerWhy} onChange={(event) => updateCensus({ tellWorkerWhy: event.target.value as CensusResponseRules['tellWorkerWhy'] })}>
                  <option value="policy">The policy in words</option>
                  <option value="none">The fact alone</option>
                </select>
              </label>
              <label className="inline-flex items-center gap-2 font-bold">
                Tolerance
                <input type="number" step={0.05} min={0} placeholder="A shift" className="w-20 rounded border border-marine/30 bg-white px-2 py-1.5 text-right text-sm font-bold text-ink placeholder:font-normal focus:outline-none focus:border-marine" value={census.hppdTolerance ?? ''} onChange={(event) => updateCensus({ hppdTolerance: event.target.value === '' ? null : Math.max(0, Number(event.target.value)) })} />
                <span className="font-normal text-ink-mid">HPPD</span>
              </label>
            </div>
          </details>
        </AutomationGroup>

        <AutomationGroup title="Open shift lifecycle" help="facilities.automation" intro="An open shift is a seat nobody holds for an unplanned reason - a call-out, a census rise, a seat the roster could not cover - and the engine works it from the moment it is born until the shift starts, telling workers as the clock runs and asking you only when its own moves are spent. Only eligible workers are ever told: right role or substitution, unit, credentials in date, not already working, under the hours rules, still on the roster.">
          <div className="grid gap-2.5 md:grid-cols-2">
            <ChoiceCard name="open-board-mode" checked={coverage.openBoardMode === 'first_come'} title="First come" recommended onSelect={() => updateCoverage({ openBoardMode: 'first_come' })}>
              Every eligible worker may take a seat from the day it is posted; the first to accept gets it, and the race runs to the shift’s start. Nobody assigns a worker by hand, so no bias can be alleged. In the last hours Needs you offers a Clinical Bridge callout and run short.
            </ChoiceCard>
            <ChoiceCard name="open-board-mode" checked={coverage.openBoardMode === 'facility_order'} title="The facility’s order" onSelect={() => updateCoverage({ openBoardMode: 'facility_order' })}>
              One worker at a time in the offer order below, each offer for so many hours; inside the urgent line everyone eligible first come; in the last hours your override assigns, with a reason code.
            </ChoiceCard>
          </div>
          <ol className="flex flex-col gap-1.5 text-xs text-ink">
            <li className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="w-28 font-bold">1 · Posted</span>
              <InlineNumber label="Posted" value={coverage.openBoardOpensDaysBefore} onChange={(value) => updateCoverage({ openBoardOpensDaysBefore: Math.max(0, Math.round(value)) })} suffix="days before the shift" />
              {coverage.openBoardMode === 'first_come' ? (
                <span className="inline-flex items-center gap-2 text-[11px] text-ink-mid">
                  · each worker gets one daily digest of the seats newly open to them, a seat still open repeated every
                  <OptionalNumber label="Repeat a seat in the digest every" value={coverage.nudgeEveryHours} min={1} onChange={(nudgeEveryHours) => updateCoverage({ nudgeEveryHours })} />
                  hours
                </span>
              ) : (
                <span className="text-[11px] text-ink-mid">· offered in turn in the order below</span>
              )}
            </li>
            <li className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="w-28 font-bold">2 · Urgent from</span>
              <InlineNumber label="Urgent from" value={coverage.openBoardClosesHoursBefore} onChange={(value) => updateCoverage({ openBoardClosesHoursBefore: Math.max(0, value) })} suffix="hours before" />
              <span className="inline-flex items-center gap-2 text-[11px] text-ink-mid">
                · everyone eligible told at once, through quiet hours, and again every
                <OptionalNumber label="Repeat the urgent notice every" value={coverage.urgentRepeatHours} min={1} onChange={(urgentRepeatHours) => updateCoverage({ urgentRepeatHours })} />
                hours to those who have not answered
              </span>
            </li>
            <li className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="w-28 font-bold">3 · Bridge callout</span>
              <OptionalNumber label="The engine opens a Clinical Bridge callout" value={coverage.bridgeCalloutHoursBefore} min={0} onChange={(bridgeCalloutHoursBefore) => updateCoverage({ bridgeCalloutHoursBefore })} />
              <span className="text-[11px] text-ink-mid">hours before · the engine opens the callout itself where Clinical Bridge is on; off leaves it to a person</span>
            </li>
            <li className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="w-28 font-bold">4 · Lock</span>
              <InlineNumber label="Lock" value={coverage.lockHoursBefore} onChange={(value) => updateCoverage({ lockHoursBefore: Math.max(0, value) })} suffix="hours before" />
              <span className="text-[11px] text-ink-mid">· the seat comes to Needs you; swaps and cancellations stop{coverage.openBoardMode === 'first_come' ? '; the race runs on' : '; only your override assigns'}</span>
            </li>
            <li className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="w-28 font-bold">5 · Started</span>
              <span className="text-[11px] text-ink-mid">a seat still open is closed as ended unfilled and logged; Needs you reminds you to record it ran short, or leave it, for</span>
              <InlineNumber label="Reminder after a seat ended unfilled" value={coverage.ranShortGraceDays} onChange={(value) => updateCoverage({ ranShortGraceDays: Math.min(30, Math.max(0, Math.round(value))) })} suffix="days" />
            </li>
          </ol>
          <div className="flex flex-wrap items-center gap-2">
            <OrderPills label="Offer order" order={census.addOrder ?? DEFAULT_CENSUS_RESPONSE_RULES.addOrder ?? ALL_ADD_STEPS} all={ALL_ADD_STEPS} labels={ADD_PILL_LABELS} onChange={(addOrder) => updateCensus({ addOrder })} />
            <span className="text-[11px] text-ink-mid">drag to reorder · within a step by score · the facility’s order, and the census response’s added seats</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {coverage.openBoardMode === 'facility_order' && <InlineNumber label="Hours per offer" value={coverage.openBoardOfferHours} onChange={(value) => updateCoverage({ openBoardOfferHours: Math.max(0, value) })} />}
            <InlineNumber label="Bridge callout escalates to the board after" value={rules.bridge.escalateAfterMinutes} onChange={(value) => update({ bridge: { ...rules.bridge, escalateAfterMinutes: Math.max(0, Math.round(value)) } })} suffix="min" />
          </div>
          <span className="text-[11px] text-ink-mid">The reliability score still orders the planned month (the run), the trade board’s candidates and the Clinical Bridge boost; first come it no longer orders who is offered an open seat. The board wakes itself at each stage whether or not a screen is open.</span>
        </AutomationGroup>

        <AutomationGroup title="Telling the facility’s staff" help="facilities.automation" intro="The facility’s defaults for what its administrators and operators are told. Each person sets their own channels, number and times under Your notifications in the header; a kind switched off here reaches nobody.">
          <SwitchLine label="At once, for anything in Needs you" checked={alerts.atOnce} onChange={(atOnce) => update({ alerts: { ...alerts, atOnce } })} />
          <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-ink">
            <span>A daily digest at</span>
            <input type="time" aria-label="Digest time" className={TIME_CLASS} value={alerts.digestAt} onChange={(event) => update({ alerts: { ...alerts, digestAt: event.target.value } })} />
            <span className="font-normal text-[11px] text-ink-mid">· census, coverage, open shifts, what the engine did overnight; for anyone who set no hour of their own</span>
          </div>
          <SwitchLine label="Before the release: the month’s health, 24 hours ahead" checked={alerts.beforeRelease} onChange={(beforeRelease) => update({ alerts: { ...alerts, beforeRelease } })} />
          <SwitchLine label="A weekly summary on Mondays" checked={alerts.weekly} onChange={(weekly) => update({ alerts: { ...alerts, weekly } })} />
          <span className="text-[11px] text-ink-mid">Who is told: every facility administrator of this facility; operators who switched it on for themselves.</span>
        </AutomationGroup>
      </section>

      <div className="flex flex-wrap items-center gap-3">
        <Button icon={RotateCcw} variant="secondary" onClick={() => onChange(withAutomationDefaults(rules))}>
          Back to defaults
        </Button>
        <span className="text-[11px] text-ink-mid">Saved with the other settings tabs by Save Configuration below; every change is in the configuration history.</span>
      </div>
    </div>
  )
}
